import { Input, Output, EventEmitter, ViewChild, ElementRef, Directive } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

@Directive()
export abstract class SelectorBase<TElement, TOutput> {
    public _allElements: TElement[];
    public _initialSet: TOutput[];

    searchControl: AbstractControl = new FormControl();
    selectedSearchControl: AbstractControl = new FormControl();
    filteredAvailableItems: TElement[] = [];
    filteredSelectedItems: TElement[] = [];
    availableItems: TElement[] = [];
    availableItems_selected: TElement[] = [];
    selectedItems: TElement[] = [];
    selectedItems_selected: TElement[] = [];

    @ViewChild('available', { static: true }) selectAvailable: ElementRef;
    @ViewChild('selected', { static: true }) selectSelected: ElementRef;

    @Input('elements') set allElements(val: TElement[]) {
        this._allElements = val;
        this.setupItems();
    }

    @Input('initial-set') set initialSet(val: TOutput[]) {
        this._initialSet = val;
        this.setupItems();
    }

    @Input('group-title') groupTitle: string;
    @Input('selected-title') selectedTitle: string;
    @Input('available-title') availableTitle: string;

    @Output() changed: EventEmitter<TOutput[]> = new EventEmitter<TOutput[]>();

    abstract compareElements(a: TElement, b: TElement): number;
    abstract elementEqualsOutput(element: TElement, output: TOutput): boolean;
    abstract elementToOutput(element: TElement): TOutput;
    abstract getDisplay(element: TElement): string;

    constructor(
    ) {
        this.searchControl.valueChanges.pipe(debounceTime(100)).subscribe((result) => {
            this.filteredAvailableItems = this.filterItems(result, this.availableItems);
        });

        this.selectedSearchControl.valueChanges.pipe(debounceTime(100)).subscribe((result) => {
            this.filteredSelectedItems = this.filterItems(result, this.selectedItems);
        })
    }

    get allElements() { return this._allElements; }

    get initialSet(): TOutput[] {
        return this._initialSet;
    }

    emitTOutputs(): void {
        let selectedOutputs = this.selectedItems.map(e => this.elementToOutput(e));
        this.changed.emit(selectedOutputs);
    }

    filterItems(filteredVal: string, items: TElement[]): TElement[] {
        if (!filteredVal)
            return items || [];

        filteredVal = filteredVal.toLocaleLowerCase();
        return items.filter(x => this.getDisplay(x).toLocaleLowerCase().indexOf(filteredVal) !== -1);

    }

    makeAvailable(): void {
        let copy = Array.from(this.selectedItems_selected);
        this.availableItems = this.availableItems.concat(copy);
        this.availableItems.sort((a, b) => this.compareElements(a, b));

        for (let p of copy) {
            let index = this.selectedItems.indexOf(p);
            if (index > -1) {
                this.selectedItems.splice(index, 1);
            }
        }

        this.selectedItems_selected = [];
        this.filterSelectedItems();
        this.emitTOutputs();
    }

    makeSelected(): void {
        let copy = Array.from(this.availableItems_selected);
        this.selectedItems = this.selectedItems.concat(copy);
        this.selectedItems.sort((a, b) => this.compareElements(a, b));

        for (let p of copy) {
            let index = this.availableItems.indexOf(p);
            if (index > -1) {
                this.availableItems.splice(index, 1);
            }
        }

        this.availableItems_selected = [];
        this.filterSelectedItems();
        this.emitTOutputs();
    }

    public setupItems(): void {
        if (this.allElements && this.initialSet) {
            this.availableItems = this.allElements.filter(p => !this.initialSet.find(c => this.elementEqualsOutput(p, c)));
            this.selectedItems = this.allElements.filter(p => this.initialSet.find(c => this.elementEqualsOutput(p, c)));
            this.availableItems.sort((a, b) => this.compareElements(a, b));
            this.selectedItems.sort((a, b) => this.compareElements(a, b));
            this.filterSelectedItems();
        }
    }

    public filterSelectedItems(): void {
        this.filteredAvailableItems = this.filterItems(this.searchControl.value, this.availableItems);
        this.filteredSelectedItems = this.filterItems(this.selectedSearchControl.value, this.selectedItems);
    }

    /**
     * Clears anything selected in a multi select, if the blur event is not a permission button click
     *   helps user distinguish what's selected and available for movement
     * @param e - mouse blur event
     */
    clearSelection(e: MouseEvent): void {
        //get focus element
        let focus = <HTMLElement>e.relatedTarget;
        //make sure the focus is not the permission button
        if (!focus || focus && !focus.classList.contains("permission-button")) {
            let select;
            if (e.srcElement == this.selectAvailable.nativeElement) {
                //set proper select
                select = <HTMLSelectElement>this.selectAvailable.nativeElement;
                //clear selected array
                this.availableItems_selected.length = 0;
            } else if (e.srcElement == this.selectSelected.nativeElement) {
                select = <HTMLSelectElement>this.selectSelected.nativeElement;
                this.selectedItems_selected.length = 0;
            }
            //set index to "nothing"
            select.selectedIndex = -1;
        }
    }
}
